import { useEffect, useState } from "react";
import { BulkUploadError, useValidateBulkCSV } from "../../api/ReportUploadApi";
import BulkDataTable from "./BulkDataTable";
import { parseFile, validateDataLocal, validateHeaderColumns } from "./helpers";
import ErrorMessageContainer from "../../shared-components/ErrorMessageContainer";
import { classNames } from "../../utils";
import LoadingSpinner from "../../shared-components/LoadingSpinner";
import Button from "../../shared-components/Button";

type Props = {
  files: File[] | undefined;
  companyUUID: string;
  blueprintUUIDs: string;
  onReady: () => void;
  onValidationComplete: (hasErrors: boolean) => void;
  version: number;
};

const UploadValidationSummary = ({
  files,
  companyUUID,
  blueprintUUIDs,
  onReady,
  onValidationComplete,
  version,
}: Props) => {
  const [validateRemote, setValidateRemote] = useState(false);
  const [errorsLocal, setErrorsLocal] = useState<BulkUploadError[]>([]);
  const [errorsRemote, setErrorsRemote] = useState<BulkUploadError[]>([]);
  const [rowData, setRowData] = useState<string[][]>();
  const [fileError, setFileError] = useState<string[] | undefined>();
  const {
    mutateAsync: validateBulkCSV,
    data: validateRemoteResult,
    isLoading: isLoadingValidate,
    error: errorValidate,
  } = useValidateBulkCSV();

  useEffect(() => {
    try {
      parseFile(files).then((data) => {
        if (!data) {
          setFileError(["Please upload a valid CSV file"]);
          return;
        }
        setRowData(data.map((row) => row.map((cell) => cell.trim())));
      });
    } catch (error) {
      setFileError(["Please upload a valid CSV file"]);
    }
  }, [files]);

  useEffect(() => {
    if (!rowData) return;
    const headerError = validateHeaderColumns(rowData);

    if (headerError && (headerError ?? []).length > 0) {
      setFileError([
        "Template Version Error",
        ...headerError?.map((e) => e.message),
        "<<DOWNLOAD_TEMPLATE>>",
      ]);
      return;
    }
    const result = validateDataLocal(rowData);
    if (result && result.length > 0) {
      setErrorsLocal(result);
    }
    setValidateRemote(true);
  }, [rowData]);

  useEffect(() => {
    if (errorValidate) {
      setFileError(["Error validating file please check with support."]);
    }
  }, [errorValidate]);

  useEffect(() => {
    if (
      validateRemote &&
      files &&
      files.length > 0 &&
      blueprintUUIDs.length > 0
    ) {
      validateBulkCSV({
        file: files[0],
        blueprintUUIDs: blueprintUUIDs,
        companyUUID: companyUUID,
        version: version,
      });
    }
  }, [
    validateRemote,
    files,
    blueprintUUIDs,
    validateBulkCSV,
    companyUUID,
    version,
  ]);

  useEffect(() => {
    if (validateRemoteResult) {
      setErrorsRemote(
        validateRemoteResult.errors.map((e: BulkUploadError) => ({
          ...e,
          row: e.row + 1,
        }))
      );
    }

    if (validateRemoteResult) {
      const hasErrors =
        validateRemoteResult.errors.length > 0 || errorsLocal.length > 0;
      onValidationComplete(hasErrors);
      if (!hasErrors) {
        onReady();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateRemoteResult, errorsLocal]);

  const errors = errorsLocal.concat(errorsRemote);

  return (
    <div>
      {fileError && (
        <ErrorMessageContainer>
          <>
            {fileError.map((error, idx) =>
              error === "<<DOWNLOAD_TEMPLATE>>" ? (
                <Button
                  text="Download New Template"
                  onClick={() => {
                    window.open("/bulkload-template.csv");
                  }}
                  className="w-52 bg-red-600 mx-4 my-2"
                />
              ) : (
                <div
                  className={classNames(
                    idx > 0
                      ? "pl-4 my-2 text-sm text-gray-600"
                      : "pl-4 my-2 font-semibold text-gray-700"
                  )}
                >
                  {error}
                </div>
              )
            )}
          </>
        </ErrorMessageContainer>
      )}
      {isLoadingValidate && <LoadingSpinner />}
      {!fileError && !isLoadingValidate && (
        <BulkDataTable data={rowData ?? []} errors={errors} />
      )}
    </div>
  );
};

export default UploadValidationSummary;
